import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, timer } from 'rxjs';
import { debounce, filter } from 'rxjs/operators';
import { UiNotificationService } from '@/ui-notifications/ui-notification.service';
import { JWTTokenService } from './jwt-token.service';
import { LocalStorageService } from './local-storage.service';
import { LOGOUT_SENTINEL_KEY, LogOutService } from './log-out.service';
import { TimeClockService } from '@/reviewer/modules/reviewer-clock/time-clock.service';
import { UserSettingsStore } from '@/account/stores/user-settings.store';
import { TrackingService } from '@core/services/tracking.service';
import { DialogServicesRegistry } from '@core/services/dialogs';
import { ConfirmationService } from 'primeng/api';

export const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please sign in again.';

/** What noticed the idle session — recorded in diagnostics only. */
export type InactivityTrigger =
  | 'interval'
  | 'resume'
  | 'interceptor'
  | 'guard'
  | 'refresh_failed'
  | 'manual'
  | 'storage';

export type SessionEndReason = 'timeout' | 'other-tab';

interface InactiveSignal {
  next: string;
  trigger: InactivityTrigger;
}

/**
 * Watches the session end: idle timeout (interval + tab resume) and sign-out from another tab.
 * Root singleton, created by the first HTTP request through InactivityInterceptor.
 */
@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class InactivityService implements OnDestroy {
  private readonly lastActivityDateKey: string = 'last_activity_date';
  private minutesBeforeTimeOut: number = 15;
  private readonly dialogDebounceMs = 200;
  /** 5 s is far below anything a user notices; the resume listeners cover hidden tabs. */
  private readonly checkIntervalMs = 5000;
  private readonly forcedActivityIntervalMs = 5000;

  private _inactiveSubject = new Subject<InactiveSignal>();

  private timeoutModalShown = false;

  /** When the document last went hidden; null while visible. Used for resume diagnostics. */
  private hiddenSince: number | null = null;

  /** Last time the resume handler acted on a visible transition; throttles the wake-up burst. */
  private lastResumeAt = 0;

  // Stored as fields so ngOnDestroy removes exactly these listeners (pattern: ChatPresenceService).
  private readonly resumeHandler = (event: Event) => {
    if (document.visibilityState !== 'visible') {
      this.hiddenSince ??= Date.now();
      return;
    }
    const now = Date.now();
    if (now - this.lastResumeAt < 1000) {
      return;
    }
    this.lastResumeAt = now;
    const hiddenForSeconds = this.hiddenSince
      ? Math.round((Date.now() - this.hiddenSince) / 1000)
      : 0;
    this.hiddenSince = null;
    const stale = this.shouldTimeOutUser();
    // Short tab switches are noise: breadcrumb only. A stale session escalates to a message.
    this.tracking.trackSession(
      'resume',
      { trigger: event.type, hiddenForSeconds, stale },
      { breadcrumbOnly: !stale }
    );
    this.checkNow('resume');
  };

  private readonly storageHandler = (event: StorageEvent) =>
    this.onStorage(event);

  private interval!: ReturnType<typeof setInterval>;
  private forcedActivityPeriodInterval?: ReturnType<typeof setInterval>;

  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly notificationService: UiNotificationService,
    private readonly logOutService: LogOutService,
    private readonly jwtService: JWTTokenService,
    private readonly router: Router,
    private readonly timeClockService: TimeClockService,
    private readonly settingsStore: UserSettingsStore,
    private readonly tracking: TrackingService,
    private readonly dialogsRegistry: DialogServicesRegistry,
    private readonly confirmationService: ConfirmationService,
    private readonly zone: NgZone
  ) {
    if (document.visibilityState !== 'visible') {
      this.hiddenSince = Date.now();
    }

    // Timers and DOM listeners outside the zone: a periodic tick must not trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.interval = setInterval(
        () => this.checkNow('interval'),
        this.checkIntervalMs
      );
      document.addEventListener('visibilitychange', this.resumeHandler);
      window.addEventListener('focus', this.resumeHandler);
      window.addEventListener('pageshow', this.resumeHandler);
      window.addEventListener('storage', this.storageHandler);
    });

    this._inactiveSubject
      .asObservable()
      .pipe(
        untilDestroyed(this),
        debounce(() => timer(this.dialogDebounceMs)),
        filter(() => !this.timeoutModalShown)
      )
      .subscribe((signal) =>
        this.forceSignOut(signal.next, 'timeout', signal.trigger).catch((err) =>
          this.tracking.captureException(err, {
            extra: { message: 'Session sign-out failed' },
          })
        )
      );

    this.settingsStore.minutesBeforeTimeOut$
      .pipe(untilDestroyed(this))
      .subscribe((minutes) => {
        this.minutesBeforeTimeOut = minutes;
      });
  }

  ngOnDestroy(): void {
    clearInterval(this.interval);
    this.endForcedActivityPeriod();
    document.removeEventListener('visibilitychange', this.resumeHandler);
    window.removeEventListener('focus', this.resumeHandler);
    window.removeEventListener('pageshow', this.resumeHandler);
    window.removeEventListener('storage', this.storageHandler);
  }

  /** Evaluates the idle timeout immediately. Safe to call from outside the Angular zone. */
  checkNow(trigger: InactivityTrigger = 'interval'): void {
    if (!this.shouldTimeOutUser()) {
      return;
    }
    this.zone.run(() => this.recordInactive(undefined, trigger));
  }

  recordInactive(next?: string, trigger: InactivityTrigger = 'manual') {
    // Capture the return url now: by the time the debounce fires, a synchronous logout
    // (InactivityInterceptor) may already have moved the router to /auth/login.
    this._inactiveSubject.next({ next: next ?? this.router.url, trigger });
  }

  recordActivity(): void {
    this.localStorageService.set(
      this.lastActivityDateKey,
      new Date().toUTCString()
    );
  }

  shouldTimeOutUser(): boolean {
    const lastActivityDateString = this.localStorageService.get(
      this.lastActivityDateKey
    );
    const lastActivityDate = Date.parse(lastActivityDateString!);

    const millisecondsInSecond = 1000;
    const secondsInMinute = 60;
    const diffInMinutes =
      (Date.now() - lastActivityDate) /
      (millisecondsInSecond * secondsInMinute);

    const shouldTimeOut =
      !!lastActivityDateString && diffInMinutes > this.minutesBeforeTimeOut;
    return shouldTimeOut && this.timeClockService.canTimeOut();
  }

  showTimeOutModal(): Promise<void> {
    return new Promise((resolve) => {
      this.notificationService.error(SESSION_EXPIRED_MESSAGE, {
        onSubmit: () => resolve(),
        onClose: () => resolve(),
        size: 'dialog-sm !w-[235px]',
      });
    });
  }

  isActive(): boolean {
    return !this.jwtService.jwtToken || !this.shouldTimeOutUser();
  }

  tryRecordActivity(
    next?: string,
    trigger: InactivityTrigger = 'manual'
  ): boolean {
    if (!this.jwtService.jwtToken) {
      return true;
    }

    const isActive = !this.shouldTimeOutUser();
    if (isActive) {
      this.recordActivity();
    } else {
      this.recordInactive(next, trigger);
    }
    return isActive;
  }

  beginForcedActivityPeriod(): void {
    this.endForcedActivityPeriod();
    this.zone.runOutsideAngular(() => {
      this.forcedActivityPeriodInterval = setInterval(
        () => this.recordActivity(),
        this.forcedActivityIntervalMs
      );
    });
  }

  endForcedActivityPeriod(): void {
    if (this.forcedActivityPeriodInterval) {
      clearInterval(this.forcedActivityPeriodInterval);
      this.forcedActivityPeriodInterval = undefined;
    }
  }

  private onStorage(event: StorageEvent): void {
    // Only the explicit sentinel counts. `key === null` is also raised by localStorage.clear()
    // from impersonation and the guards, where storage is repopulated right after.
    if (event.key !== LOGOUT_SENTINEL_KEY || !event.newValue) {
      return;
    }
    if (!this.hasActiveSession()) {
      return;
    }
    this.zone.run(() =>
      this.forceSignOut(this.router.url, 'other-tab', 'storage').catch((err) =>
        this.tracking.captureException(err, {
          extra: { message: 'Session sign-out failed' },
        })
      )
    );
  }

  private hasActiveSession(): boolean {
    // localStorage is already empty here (the other tab cleared it), so judge from in-memory state.
    // This is also the loop breaker: after our own logout the signal is null, so the sentinel our
    // teardown re-writes never bounces back into us.
    return (
      this.jwtService.currentUserSignal() !== null &&
      !this.router.url.startsWith('/auth')
    );
  }

  /** Single teardown for every way a session can end; guarded so concurrent triggers show one modal. */
  private async forceSignOut(
    next: string,
    reason: SessionEndReason,
    trigger: InactivityTrigger
  ): Promise<void> {
    if (this.timeoutModalShown) {
      return;
    }
    this.timeoutModalShown = true;
    try {
      if (reason === 'timeout') {
        const lastActivityDate = this.localStorageService.get(
          this.lastActivityDateKey
        );
        this.tracking.trackSession('timeout_detected', {
          trigger,
          thresholdMinutes: this.minutesBeforeTimeOut,
          idleMinutes: lastActivityDate
            ? Math.round((Date.now() - Date.parse(lastActivityDate)) / 60_000)
            : null,
          lastActivityDate,
          visibilityState: document.visibilityState,
          next,
        });
      } else {
        this.tracking.trackSession('cross_tab_logout', { trigger, next });
      }
      // Hide all dialogs before logging out
      for (const dialog of this.dialogsRegistry.services.values()) {
        dialog.closeAll();
      }
      this.confirmationService.close();
      await this.logOutService.logout(next, {
        notifyServer: reason === 'timeout',
        reason,
      });
      await this.showTimeOutModal();
    } finally {
      this.timeoutModalShown = false;
    }
  }
}
